import axios from "axios";

export type UploadProgress = {
  percent: number;
  loaded: number;
  total: number;
};

/** Run `worker` over `items`, at most `limit` at a time; results keep input order. */
export const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const at = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop -- each runner drains the queue one item at a time
      results[at] = await worker(items[at], at);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return results;
};

/**
 * PUT a file to a GCS resumable-upload session the API opened. Bare `axios`,
 * not `axiosInstance`: the Firebase bearer token must not be sent to GCS, and
 * the session URI is the only credential it needs. The bytes never pass
 * through the API, so Cloud Run's 32 MiB request cap does not apply.
 */
export const putToUploadSession = async (
  uploadUrl: string,
  file: Blob,
  options: {
    contentType: string;
    signal?: AbortSignal;
    onProgress?: (progress: UploadProgress) => void;
  }
): Promise<void> => {
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": options.contentType },
    signal: options.signal,
    onUploadProgress: (event) => {
      const total = event.total && event.total > 0 ? event.total : file.size;
      const loaded = event.loaded ?? 0;
      const percent = total
        ? Math.max(0, Math.min(100, Math.round((loaded / total) * 100)))
        : 0;
      options.onProgress?.({ percent, loaded, total });
    }
  });
};
